import React from 'react';
import {
  Box, Heading, HStack, Card, Text, VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { Image } from 'expo-image';
import HtmlText from '@/common/components/HtmlText';
import { getColorForModule } from '@/common/utils/getColorForModule';
import GradeCard from '@/common/components/GradeCard';
import type { Module } from '../../../types';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const FALLBACK_IMAGE = require('@assets/img/fallback-module-image.jpg');

interface GradeHeaderProps {
  moduleTitle: string;
  grade: number;
  comment?: string;
  showFeedback?: boolean;
  showAdminOverrideLabel?: boolean;
  moduleImageUrl?: string;
  module: Module;
  lookingBack?: boolean;
  attachBelow?: boolean;
}

const GradeHeader: React.FC<GradeHeaderProps> = ({
  moduleTitle,
  grade,
  comment,
  showFeedback = true,
  showAdminOverrideLabel = false,
  moduleImageUrl,
  module,
  lookingBack = false,
  attachBelow = false,
}) => {
  const { t } = useTranslation();
  const moduleColor = getColorForModule(module);

  return (
    <Card w="100%" h="auto" p={0} overflow={attachBelow ? 'visible' : 'hidden'} mb={attachBelow ? 0 : '$4'}>
      {/* Top section with image and color-coded header */}
      <Box w="100%" h={80} position="relative" overflow="hidden">
        {/* Module color accent - thin bar on top */}
        <Box position="absolute" top={0} left={0} right={0} height={4} bg={`$${moduleColor}500`} zIndex={2} />

        {/* Semi-transparent overlay for better text readability */}
        <Box position="absolute" top={0} left={0} right={0} bottom={0} bg="rgba(0,0,0,0.3)" zIndex={1} />

        {/* Image */}
        <Image
          source={moduleImageUrl || FALLBACK_IMAGE}
          // eslint-disable-next-line react-native/no-inline-styles
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          }}
          contentFit="cover"
        />

        {/* Title overlay */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={2}
          justifyContent="center"
          pl="$8"
          pr="$16"
        >
          <Heading
            numberOfLines={2}
            size="xl"
            color="$white"
          >
            {moduleTitle}
          </Heading>
        </Box>
      </Box>

      {!!lookingBack && (
        <Box
          bg="$bgBrandSubtle"
          borderLeftWidth={3}
          borderLeftColor="$brandBase"
          px="$4"
          py="$3"
        >
          <Text size="sm" color="$textOnBrandSubtle">
            {t('student.grade.versions.lookingBack')}
          </Text>
        </Box>
      )}

      {/* Content section with grade card */}
      <HStack w="100%" py="$8" px="$8" justifyContent="space-between" alignItems="flex-start">
        <Box flex={1} mr="$4">
          {showFeedback && (
            comment ? (
              <HtmlText
                content={comment}
                numberOfLines={undefined}
                fontSize={14}
                contentStyle={{}}
                applySubmissionFormatting={false}
                testID="grade-module-feedback"
              />
            ) : (
              !showAdminOverrideLabel && (
                <Text color="$textSubtle" fontSize={14}>
                  No feedback provided for this module.
                </Text>
              )
            )
          )}
        </Box>

        {/* Right: Grade Card */}
        <VStack alignItems="flex-end" space="xs">
          <GradeCard grade={grade} size="md" />
          {showAdminOverrideLabel && (
            <Text size="sm" color="$textSubtle" testID="grade-admin-override-label">
              {t('student.grade.adminOverride.label')}
            </Text>
          )}
        </VStack>
      </HStack>
    </Card>
  );
};

export default GradeHeader;
